import { ICommentAdConfiguration } from '../../domain/conf/ads'
import { Utils } from '../../utils/common'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { IdentityEvent, IIdentityEvent } from '../common/events/identity'
import { IProfile, Profile } from '../common/profile'
import { IIdentityLinksConfig, IIdentityProviderConfig, ISubscriberProviderConfig, ISubscriberServiceAdapter
    } from './adapters/adapter'

export interface IIdentityService {
    initialize(...args: any[]): void
    update(profile: IProfile, store: boolean): void
}

export class IdentityService extends SimpleEvents implements IIdentityService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: Thgit branchese will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            btns:        'data-sso-btns',
            close:       'data-id-close',
            marker:      'data-sso-marker',
            name:        'data-id-name',
            signIn:      'data-id-login',
            signOut:     'data-id-logout',
        },
        class: {
            accountMenu: 'my-account__menu',
            active:      'active',
            adContainer: 'ad__container',
            myAccount:   'my-account__container',
            signedIn:    'sso-signedIn',
            signedOut:   'sso-signedOut',
            subscriptionAlert: 'tp-container-inner',
        },
        links: {},
        logo: '',
        property: ''
    }
    public profile: IProfile

    protected hashCode: number
    protected hasSubscriptions: boolean
    protected hasRegistrantTerm: boolean
    protected hasRegistrantResId: boolean
    protected subscriptionClientId: string
    protected subscriptionAppId: string
    protected subscriptionRegistrantResId: string

    /**
     * Adds DOM event listeners.
     */
    public constructor(private adapter: ISubscriberServiceAdapter) {
        super()

        // fetch profile from per-domain store, proceed anonymously if none found
        const localProfile = LocalStore.get<IProfile>(Store.ID)
        this.update(localProfile || new Profile()).catch(console.log)

        // attempt to fetch profile from global (cross-domain) store
        GlobalStore.get<Profile>(Store.ID)
            .then((profile) => {
                // update relevant data if same user
                if (profile && profile.email === this.profile.email) {
                    this.update({
                        ageOfMajority: profile.ageOfMajority,
                        id: profile.id
                    } as IProfile).catch(console.log)
                }
            })
            .catch(console.log)
    }

    /**
     * Initializes adapter, adds listeners.
     *
     * @param {IdentityProviderConfig} ssoCreds for SSO provider
     * @param {IdentityProviderConfig} subscriberCreds for subscriptions provider
     */

    public initialize(
        ssoCreds: IIdentityProviderConfig,
        subscriberCreds: ISubscriberProviderConfig,
        links: IIdentityLinksConfig,
        logo: string,
        isPremium = false,
        isPaywallWhitelisted = false,
        property = '',
        commentAdConfiguration?: ICommentAdConfiguration
        ) {

        IdentityService.DOM.links = links
        IdentityService.DOM.logo = logo
        IdentityService.DOM.property = property

        this.hasSubscriptions = !!subscriberCreds
        this.hasRegistrantTerm = this.hasSubscriptions ? !!subscriberCreds.registrantTermId : false
        this.subscriptionClientId = this.hasSubscriptions ? subscriberCreds.registrantTermId : ''
        this.subscriptionAppId = this.hasSubscriptions ? subscriberCreds.account : ''
        this.hasRegistrantResId = this.hasSubscriptions ? !!subscriberCreds.registrantResId : false
        this.subscriptionRegistrantResId = this.hasRegistrantResId ? subscriberCreds.registrantResId: ''

        if (ssoCreds) {
            this.adapter
                .addEventListener(IdentityEvent.EMAIL_VERIFIED, this.emailVerified.bind(this))
                .addEventListener(IdentityEvent.LOGIN_MODAL_READY, this.loginModalReady.bind(this))
                .addEventListener(IdentityEvent.IDENTITY_CHANGE, (e: IIdentityEvent) => this.update(e.detail))
                .addEventListener(IdentityEvent.SESSION_INIT, (e: IIdentityEvent<boolean>) => this.session(e.detail))
                // NOTE: traits are called first, so this corresponds to `WithViafoura` signature
                .initialize(subscriberCreds, ssoCreds, isPremium, isPaywallWhitelisted, commentAdConfiguration)
        }

        // fire id init event
        this.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_INIT, this.profile))

        return this
    }

    /**
     * Email verified event from SSO provider.
     * Just relaying atm. Manipulate profile here if needed (not attached to event).
     */
    public emailVerified(e: IIdentityEvent) {
        this.dispatchEvent(e)
    }

    public async update(data: IProfile, dispatchEvent = true) {
        // outdated data, exit
        if (data.updated && this.profile && data.updated < this.profile.updated) {
            return
        }

        // merge data
        this.profile = { ...this.profile, ...data }

        // profile unchanged
        if (this.hashCode && Utils.hashCode(Utils.prune(this.profile)) === this.hashCode) {
            return
        }

        // update modified time
        if (this.hashCode || !this.profile.updated) {
            this.profile.updated = Date.now()
        }

        // update hash (need to set before async actions so can ignore when no changes)
        this.hashCode = Utils.hashCode(Utils.prune(this.profile))

        // get subscriptions
        await this.updateSubscriptions()

        // check for one-digital conversions
        await this.updateConversions()

        // update hash
        this.profile.updated = Date.now()
        this.hashCode = Utils.hashCode(Utils.prune(this.profile))

        // dispatch event
        if (dispatchEvent) {
            this.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, this.profile))
        }

        // update UI
        this.updateUI().catch(console.log)

        // store
        this.storeProfile()
    }

    /**
     * Enable sign-in link when adaptor is ready
     */
    public async loginModalReady() {
        await DOMUtils.ready

        /* register listeners only when janrain finished loading */
        this.addListeners()

        /* remove sign in loader */
        const signInEl: NodeListOf<HTMLElement> = document.querySelectorAll(`[${IdentityService.DOM.attrs.signIn}]`)
        if (signInEl.length && signInEl.length > 0) {
            signInEl[0].removeAttribute('style')
            signInEl[0].className = 'signin-button--modal-ready'
            signInEl[0].setAttribute('aria-disabled', 'false')
            const loaderDiv = document.getElementsByClassName('signInLoader')
            if (loaderDiv.length && loaderDiv.length > 0) {
                signInEl[0].removeChild(loaderDiv[0])
            }
        }
    }

    /**
     * Check if subscribed to the property (Not cross-app access)
     */
    public subscribed() {
        const propertySubscriber = (this.profile.subscriptions || []).filter((subscription) => {
            return subscription.aid === this.subscriptionAppId && subscription.id !== this.subscriptionRegistrantResId
        })
        return (propertySubscriber.length > 0) ? true : false
    }

    /**
     * Handles session init event from SSO provider.
     * NOTE: Incoming `Event.detail` changed from `IProfile` to `boolean`
     * due to Janrain issue where can have session but no user data. Still
     * propagating as `IProfile` as per desired behaviour.
     */
    protected async session(exists: boolean) {
        let profile = this.profile

        // signed in but no user data or signed out but have user data, refresh from adapter
        if (exists && !profile.email || !exists && profile.email) {
            profile = {
                ...profile,
                ...this.adapter.fetch()
            }
        }

        this.dispatchEvent(Utils.event(IdentityEvent.SESSION_INIT, profile))
        await this.update(profile)
    }

    /**
     * Registers listeners for required DOM events.
     *
     * TODO: expand as implementation requires.
     */
    private addListeners() {
        // #if(document){ return }
        document.addEventListener('click', (e: Event) => {
            const el = e.target as Element

            if (el.hasAttribute(IdentityService.DOM.attrs.signIn)) {
                this.adapter.signIn()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.signOut)) {
                this.adapter.signOut()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.close)) {
                this.adapter.closeModal()
            }
        })
    }

    /**
     * Sets name in UI (based on attr). Hides for anonymous users.
     *
     * @param {string} name
     */
    private async setName(name?: string) {
        await DOMUtils.ready
        DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.name}]`))
            .forEach((el: Element) => {
                DOMUtils.setVis(el, !!name)
                el.innerHTML = name
            })
    }

    /**
     * If one digital, checks to see if user needs to be added to registrant term.
     */
    private async updateConversions() {
        if (this.profile.email) {
            if (this.profile.conversions === undefined && this.hasRegistrantTerm ) {
                const conversions = await this.adapter.getConversions()
                this.profile.conversions = conversions

                if (this.profile.conversions.indexOf(this.subscriptionClientId) === -1) {
                    // no record of having registered this user for basic digital access on this one-digital property
                    await this.adapter.registerForOneDigital().catch(console.log)
                    this.profile.conversions = [...this.profile.conversions, this.subscriptionClientId]
                }
            }
        } else {
            this.profile.conversions = undefined
        }
    }

    /**
     * Gets subscriptions.
     */
    private async updateSubscriptions() {
        if (this.profile.email) {
            if (this.profile.subscriptions === undefined && this.hasSubscriptions) {

                // direct assignment causes issues in Mac FF/Saf (others?):
                // this.profile.subscriptions = await this.adapter.getAccess()

                // ...but intermediate assignment works:
                const access = await this.adapter.getAccess()
                this.profile.subscriptions = access
            }
        } else {
            this.profile.subscriptions = undefined
        }
    }

    /**
     * Stores profile; global properties only (e.g. not 'adLight' since per-config)
     */
    private storeProfile() {
        // clean
        const profileStore = Utils.prune(this.profile)

        // ad light is determined by page conf
        delete profileStore.adLight

        // store
        LocalStore.set(Store.ID, profileStore)

        // subscriptions vary by property
        delete profileStore.subscriptions
        GlobalStore.set(Store.ID, profileStore).catch(console.log)
    }

    /**
     * Check if user is currently signed in
     */
    private signedIn() {
       return this.profile.email ?  true : undefined
    }

    /**
     * Updates UI (buttons etc.). Called after state change etc.
     */
    private async updateUI() {
        await DOMUtils.ready

        const adLight = this.profile.adLight
        const subscribed = this.subscribed()

        if (adLight) {
            this.adapter.removeCommentAdUI()
            this.removeAdSlotElements()
        }

        this.setName(this.profile.email ? this.profile.name : undefined).catch(console.log)

        if(this.signedIn()) {
            // Remove the article counter
            if (adLight || subscribed) {
                this.removeSubscriptionAlert('article-counter--visible')
            }

            // Remove the register counter
            this.removeSubscriptionAlert('register-counter--visible')

            // Remove register hard gate
            if (document.body.classList.contains('register-modal--visible')) {
                document.body.classList.remove('register-modal--visible')
                this.adapter.pianoCloseModal()
            }

            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`))
            DOMUtils.updateTabs(document.querySelectorAll(
                `.${IdentityService.DOM.class.myAccount}`))
        } else {
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`))
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `.${IdentityService.DOM.class.myAccount}`), -1)
        }
        if(document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}]`).length) {
            // find DOM items based on marker specified above
            // replace class show/hide for animation rather than display block/none
            DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}], body`))
                .forEach((el: Element) => {
                    // replace signed in classes based on status
                    if(!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedIn,
                            IdentityService.DOM.class.signedOut)
                    } else if(!!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedOut,
                            IdentityService.DOM.class.signedIn)
                    }
                })
        } else {
            // display block/none method currently used
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signIn}]`),
                    !this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signOut}]`),
                    !!this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `.${IdentityService.DOM.class.myAccount}`),
                    !!this.signedIn())
        }
    }

    /**
     * Retrieve all ad slots by the container from DOM and delete
     * leaving graphical line separator from the ad section.
     */
    private removeAdSlotElements() {
        const adSlots = DOMUtils.nodes(document.querySelectorAll(`.${IdentityService.DOM.class.adContainer}`))
        adSlots.forEach((el: Element) => el.remove())
    }

    /**
     * Delete subscription alert message
     *
     * @params {string} className
     */
    private removeSubscriptionAlert(className: string) {
        if (document.body.classList.contains(className)) {
            document.querySelector(`.${IdentityService.DOM.class.subscriptionAlert}`).remove()
            document.body.classList.remove(className)

            // let other UI elements know of removal
            window.dispatchEvent(
                new CustomEvent('articleLimitAlertEvent', {
                    detail: {
                    limit: false
                    }
                })
            )
        }
    }
}
